import { Graphics, Text, Sprite, Texture } from 'pixi.js'
import { Entity } from './Entity'
import { Building } from './Building'
import { UnitType, UNIT_TYPES, getUnitType } from '../data/units'
import { Faction, getFaction } from '../data/factions'
import { distance, normalize, scale, add, Vector2 } from '../utils/math'
import { AssetLoader } from '../engine/AssetLoader'

export type UnitState = 'idle' | 'moving' | 'attacking' | 'gathering' | 'returning' | 'building'

export interface UnitCommand {
  type: 'move' | 'attack' | 'gather' | 'build' | 'stop'
  target?: Entity | Vector2
  buildingType?: string
}

export class Unit extends Entity {
  unitType: UnitType
  faction: Faction

  // Stats (with faction modifiers)
  attack: number
  attackSpeed: number
  speed: number
  range: number
  sight: number

  // State
  state: UnitState = 'idle'
  path: Vector2[] = []
  target: Entity | null = null
  gatherTarget: Vector2 | null = null
  buildTarget: { x: number; y: number; type: string } | null = null

  // Gathering
  carrying: number = 0
  carryType: 'compute' | 'data' | 'energy' | null = null
  maxCarry: number = 10

  // Combat
  attackCooldown: number = 0

  // Visual
  private bodyGraphic: Graphics
  private iconText: Text
  private sprite: Sprite | null = null
  private useSprite: boolean = false

  constructor(
    typeId: string,
    x: number,
    y: number,
    isPlayer: boolean,
    factionId: string
  ) {
    const unitType = getUnitType(typeId)
    const faction = getFaction(factionId)

    // Calculate HP with faction bonus
    const hp = Math.floor(unitType.hp * (faction.bonuses.unitPower || 1))

    super(x, y, hp, isPlayer)

    this.unitType = unitType
    this.faction = faction

    // Apply faction modifiers to stats
    this.attack = Math.floor(unitType.attack * (faction.bonuses.unitPower || 1))
    this.attackSpeed = unitType.attackSpeed
    this.speed = unitType.speed * (faction.bonuses.unitSpeed || 1)
    this.range = unitType.range
    this.sight = unitType.sight

    // Create visuals
    this.bodyGraphic = new Graphics()
    this.bodyGraphic.zIndex = 0
    this.container.addChild(this.bodyGraphic)

    this.iconText = new Text({
      text: this.getIcon(),
      style: {
        fontSize: 20,
        fill: 0xffffff
      }
    })
    this.iconText.anchor.set(0.5, 0.5)
    this.iconText.zIndex = 1
    this.container.addChild(this.iconText)

    // Try to load sprite
    const texture = AssetLoader.getUnitTexture(typeId, factionId)
    if (texture) {
      this.sprite = new Sprite(texture)
      this.sprite.anchor.set(0.5, 0.5)
      // Scale sprite to fit unit size (48x48 approx)
      const targetSize = 48
      this.sprite.scale.set(targetSize / texture.width, targetSize / texture.height)
      this.sprite.zIndex = 0
      this.container.addChild(this.sprite)
      this.useSprite = true
      // Hide fallback graphics when using sprite
      this.bodyGraphic.visible = false
      this.iconText.visible = false
    }

    // Set up selection ring
    this.drawSelectionRing(18, 0x00ff00)
    this.updateHealthBar()
    this.render()
  }

  private getIcon(): string {
    // Use emoji for now, can be replaced with sprites
    switch (this.unitType.id) {
      case 'WORKER': return '⚙'
      case 'SCOUT': return '◇'
      case 'WARRIOR': return '◆'
      case 'ARCHER': return '△'
      case 'REASONER': return '⬡'
      case 'HERO_OPUS': return '★'
      case 'HERO_SONNET': return '✦'
      case 'HERO_HAIKU': return '✧'
      default: return '○'
    }
  }

  update(dt: number): void {
    if (this.isDead) return

    // Update attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= dt
    }

    // Handle different states
    switch (this.state) {
      case 'moving':
        this.updateMovement(dt)
        break
      case 'attacking':
        this.updateCombat(dt)
        break
      case 'gathering':
        this.updateGathering(dt)
        break
      case 'returning':
        this.updateReturning(dt)
        break
      case 'building':
        this.updateBuilding(dt)
        break
    }

    // Update container position
    this.setPosition(this.x, this.y)
  }

  private updateMovement(dt: number): void {
    if (this.path.length === 0) {
      this.state = 'idle'
      return
    }

    const target = this.path[0]
    const dx = target.x - this.x
    const dy = target.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist < 5) {
      this.path.shift()
      if (this.path.length === 0) {
        this.state = 'idle'
      }
    } else {
      const moveSpeed = this.speed * 48 * dt  // 48 = tile size
      const moveX = (dx / dist) * Math.min(moveSpeed, dist)
      const moveY = (dy / dist) * Math.min(moveSpeed, dist)

      this.x += moveX
      this.y += moveY
    }
  }

  private updateCombat(dt: number): void {
    if (!this.target || this.target.isDead) {
      console.log('Combat: target dead or null, going idle')
      this.target = null
      this.state = 'idle'
      return
    }

    // For buildings, use distance to edge instead of center
    let dist: number
    if (this.target instanceof Building) {
      dist = this.target.distanceToEdge(this.x, this.y)
    } else {
      dist = this.distanceTo(this.target)
    }
    const attackRange = this.range * 48  // Convert tiles to pixels

    if (dist <= attackRange) {
      // In range, attack
      if (this.attackCooldown <= 0) {
        console.log(`Combat: In range (${dist} <= ${attackRange}), attacking!`)
        this.performAttack()
      }
    } else {
      // Need to move toward target - use path if we have one, otherwise request one
      if (this.path.length > 0) {
        this.updateMovement(dt)
        this.state = 'attacking'  // Stay in attacking state, not moving
      } else if (this.onNeedPath) {
        // Request a path to the target from Game class
        console.log(`Combat: Out of range (${dist} > ${attackRange}), requesting path to (${this.target.x}, ${this.target.y})`)
        this.onNeedPath(this, this.target.x, this.target.y)
      } else {
        console.log('Combat: No path and no onNeedPath callback!')
      }
    }
  }

  // Store last gather position so we can return after depositing
  private lastGatherTarget: Vector2 | null = null

  private updateGathering(dt: number): void {
    if (!this.gatherTarget) {
      this.state = 'idle'
      return
    }

    const dist = this.distanceToPoint(this.gatherTarget.x, this.gatherTarget.y)

    if (dist < 24) {
      // At resource, gather
      this.carrying += dt * (this.faction.bonuses.gatherSpeed || 1) * 3

      if (this.carrying >= this.maxCarry) {
        this.carrying = this.maxCarry
        // Save gather position before returning
        this.lastGatherTarget = { x: this.gatherTarget.x, y: this.gatherTarget.y }
        this.state = 'returning'
      }
    } else {
      // Move to resource
      this.moveToward(this.gatherTarget.x, this.gatherTarget.y, dt)
    }
  }

  private updateReturning(dt: number): void {
    // Returning is now handled by Game class which moves unit toward base
    // and deposits resources when close enough
  }

  // Getter for last gather target (used by Game for auto-return logic)
  getLastGatherTarget(): Vector2 | null {
    return this.lastGatherTarget
  }

  private updateBuilding(dt: number): void {
    // Construction handled by Game class
    this.state = 'idle'
  }

  private moveToward(targetX: number, targetY: number, dt: number): void {
    const dx = targetX - this.x
    const dy = targetY - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist > 5) {
      const moveSpeed = this.speed * 48 * dt
      const moveX = (dx / dist) * Math.min(moveSpeed, dist)
      const moveY = (dy / dist) * Math.min(moveSpeed, dist)

      this.x += moveX
      this.y += moveY
    }
  }

  private performAttack(): void {
    if (!this.target) return

    this.target.takeDamage(this.attack)
    this.attackCooldown = 1 / this.attackSpeed

    // Trigger attack effect (handled externally via callback)
    if (this.onAttack) {
      this.onAttack(this, this.target)
    }
  }

  // Callback for external effects
  onAttack?: (attacker: Unit, target: Entity) => void
  // Callback to request pathfinding from Game class
  onNeedPath?: (unit: Unit, targetX: number, targetY: number) => void

  // Commands
  moveTo(path: Vector2[]): void {
    this.path = path
    this.state = 'moving'
    this.target = null
    this.gatherTarget = null
  }

  attackTarget(target: Entity): void {
    console.log(`Unit ${this.unitType.name} attacking target at (${target.x}, ${target.y})`)
    this.target = target
    this.state = 'attacking'
    this.path = []
    this.gatherTarget = null
  }

  gatherFrom(x: number, y: number, resourceType: 'compute' | 'data' | 'energy'): void {
    if (!this.unitType.canGather) return

    this.gatherTarget = { x, y }
    this.carryType = resourceType
    this.state = 'gathering'
    this.path = []
    this.target = null
  }

  stop(): void {
    this.path = []
    this.target = null
    this.gatherTarget = null
    this.state = 'idle'
  }

  // Deposit carried resources (returns amount deposited)
  depositResources(): { type: string; amount: number } | null {
    if (this.carrying <= 0 || !this.carryType) return null

    const result = { type: this.carryType, amount: this.carrying }
    this.carrying = 0
    this.carryType = null
    return result
  }

  render(): void {
    // If using sprite, just update selection indicator
    if (this.useSprite && this.sprite) {
      // Tint sprite red for enemies
      if (!this.isPlayer) {
        this.sprite.tint = 0xff6666
      }

      // Selection glow
      this.bodyGraphic.clear()
      if (this.isSelected) {
        this.bodyGraphic.circle(0, 0, 26)
        this.bodyGraphic.stroke({ width: 3, color: this.faction.glowColor, alpha: 0.7 })
      }

      // Carrying indicator
      if (this.carrying > 0 && this.carryType) {
        const carryColor = this.carryType === 'compute' ? 0x3498db :
                          this.carryType === 'data' ? 0x2ecc71 : 0xf39c12
        this.bodyGraphic.circle(18, -18, 6)
        this.bodyGraphic.fill({ color: carryColor })
        this.bodyGraphic.stroke({ width: 1, color: 0xffffff })
      }
      return
    }

    // Fallback to geometric rendering
    this.bodyGraphic.clear()

    // Shadow
    this.bodyGraphic.ellipse(0, 4, 10, 5)
    this.bodyGraphic.fill({ color: 0x000000, alpha: 0.3 })

    // Body circle with faction color
    const color = this.isPlayer ? this.faction.primaryColor : 0xe74c3c
    this.bodyGraphic.circle(0, 0, 14)
    this.bodyGraphic.fill({ color })
    this.bodyGraphic.circle(0, 0, 14)
    this.bodyGraphic.stroke({ width: 2, color: this.isPlayer ? 0xffffff : 0x000000 })

    // Glow effect
    if (this.isSelected) {
      this.bodyGraphic.circle(0, 0, 16)
      this.bodyGraphic.stroke({ width: 1, color: this.faction.glowColor, alpha: 0.5 })
    }

    // Carrying indicator
    if (this.carrying > 0 && this.carryType) {
      const carryColor = this.carryType === 'compute' ? 0x3498db :
                        this.carryType === 'data' ? 0x2ecc71 : 0xf39c12
      this.bodyGraphic.circle(10, -10, 4)
      this.bodyGraphic.fill({ color: carryColor })
    }
  }
}
